/**
 * Generic map geometry + animation helpers, shared across the asset and tracker
 * maps. These are pure (no asset/tracker coupling). The asset map currently keeps
 * its own copies in `pages/assets/map/utils.tsx`; new maps should import from here.
 */

export type Coordinate = { latitude: number; longitude: number }
export type Point = { latitude: number; longitude: number }
export type Bounds = { north: number; south: number; east: number; west: number }

export const getCenterFromBounds = (bounds: Bounds): { latitude: number; longitude: number } => ({
  latitude: (bounds.north + bounds.south) / 2,
  longitude: (bounds.east + bounds.west) / 2,
})

export const calculateBoundsFromZoom = (latitude: number, longitude: number, zoom: number): Bounds => {
  const longitudeSpan = 360 / Math.pow(2, zoom)
  const latitudeSpan = 360 / Math.pow(2, zoom)
  const adjustedLongitudeSpan = longitudeSpan / Math.cos((latitude * Math.PI) / 180)
  return {
    north: latitude + latitudeSpan / 2,
    south: latitude - latitudeSpan / 2,
    east: longitude + adjustedLongitudeSpan / 2,
    west: longitude - adjustedLongitudeSpan / 2,
  }
}

const usCenter = { latitude: 39.8283, longitude: -98.5795 }

export const getUSBounds = (zoom: number): Bounds =>
  calculateBoundsFromZoom(usCenter.latitude, usCenter.longitude, zoom)

// Pick an initial zoom so the full US fits regardless of screen width.
export const getResponsiveUSZoom = (width?: number): number => {
  const w = width ?? (typeof window !== 'undefined' ? window.innerWidth : 2200)
  const zoom = Math.log2(w) - 7.1
  return Math.max(3, Math.min(4.5, zoom))
}

// Grid-based density detection: locks onto the largest cluster of points so a few
// outliers don't zoom the map all the way out.
export const findDensestRegion = (points: Point[], coverageTarget = 0.7, gridSize = 20): Point[] => {
  if (points.length <= 2) return points

  let minLat = points[0].latitude
  let maxLat = points[0].latitude
  let minLng = points[0].longitude
  let maxLng = points[0].longitude
  for (const p of points) {
    if (p.latitude < minLat) minLat = p.latitude
    if (p.latitude > maxLat) maxLat = p.latitude
    if (p.longitude < minLng) minLng = p.longitude
    if (p.longitude > maxLng) maxLng = p.longitude
  }

  const latStep = (maxLat - minLat) / gridSize || 1
  const lngStep = (maxLng - minLng) / gridSize || 1

  const grid = new Map<string, Point[]>()
  for (const p of points) {
    const row = Math.min(gridSize - 1, Math.floor((p.latitude - minLat) / latStep))
    const col = Math.min(gridSize - 1, Math.floor((p.longitude - minLng) / lngStep))
    const key = `${row},${col}`
    const bucket = grid.get(key)
    if (bucket) bucket.push(p)
    else grid.set(key, [p])
  }

  let seedKey = ''
  let seedCount = 0
  for (const [key, bucket] of grid) {
    if (bucket.length > seedCount) {
      seedCount = bucket.length
      seedKey = key
    }
  }
  if (!seedKey) return points

  const target = points.length * coverageTarget
  const selected = new Set<string>([seedKey])
  const selectedPoints: Point[] = [...(grid.get(seedKey) ?? [])]
  const neighbors: readonly [number, number][] = [
    [-1, -1],
    [-1, 0],
    [-1, 1],
    [0, -1],
    [0, 1],
    [1, -1],
    [1, 0],
    [1, 1],
  ]

  while (selectedPoints.length < target) {
    let bestKey = ''
    let bestCount = 0
    for (const key of selected) {
      const [r, c] = key.split(',').map(Number)
      for (const [dr, dc] of neighbors) {
        const nKey = `${r + dr},${c + dc}`
        if (selected.has(nKey)) continue
        const bucket = grid.get(nKey)
        if (!bucket) continue
        if (bucket.length > bestCount) {
          bestCount = bucket.length
          bestKey = nKey
        }
      }
    }
    if (!bestKey) break
    selected.add(bestKey)
    selectedPoints.push(...(grid.get(bestKey) ?? []))
  }

  return selectedPoints
}

export const zoomToCenter = (
  points: Point[],
  minZoom: number,
  maxZoom: number,
  coverageTarget = 0.7
): { bounds: Bounds; zoom: number } => {
  if (points.length === 0) {
    return { bounds: calculateBoundsFromZoom(usCenter.latitude, usCenter.longitude, 6), zoom: 6 }
  }
  if (points.length === 1) {
    return { bounds: calculateBoundsFromZoom(points[0].latitude, points[0].longitude, maxZoom), zoom: maxZoom }
  }

  const filteredPoints = findDensestRegion(points, coverageTarget)

  let minLat = filteredPoints[0].latitude
  let maxLat = filteredPoints[0].latitude
  let minLng = filteredPoints[0].longitude
  let maxLng = filteredPoints[0].longitude
  for (const point of filteredPoints) {
    minLat = Math.min(minLat, point.latitude)
    maxLat = Math.max(maxLat, point.latitude)
    minLng = Math.min(minLng, point.longitude)
    maxLng = Math.max(maxLng, point.longitude)
  }

  const centerLat = (minLat + maxLat) / 2
  const centerLng = (minLng + maxLng) / 2
  const latSpan = (maxLat - minLat) * 1.2
  const lngSpan = (maxLng - minLng) * 1.2

  if (latSpan === 0 && lngSpan === 0) {
    return { bounds: calculateBoundsFromZoom(centerLat, centerLng, maxZoom), zoom: maxZoom }
  }

  const requiredZoomForLat = Math.log2(360 / Math.max(latSpan, 0.0001))
  const cosLat = Math.cos((centerLat * Math.PI) / 180)
  const adjustedLngSpan = Math.max(lngSpan * cosLat, 0.0001)
  const requiredZoomForLng = Math.log2(360 / adjustedLngSpan)

  let zoom = Math.floor(Math.min(requiredZoomForLat, requiredZoomForLng))
  zoom = Math.max(minZoom, Math.min(maxZoom, zoom))

  return { bounds: calculateBoundsFromZoom(centerLat, centerLng, zoom), zoom }
}

/** Compute bounds + zoom to fit ALL points (no densest-cluster filtering). */
export const boundsFromAllPoints = (points: Point[], padding = 0.1): { bounds: Bounds; zoom: number } => {
  if (points.length === 0) return { bounds: getUSBounds(6), zoom: 6 }
  if (points.length === 1) {
    return { bounds: calculateBoundsFromZoom(points[0].latitude, points[0].longitude, 15), zoom: 15 }
  }

  let minLat = points[0].latitude
  let maxLat = points[0].latitude
  let minLng = points[0].longitude
  let maxLng = points[0].longitude
  for (const p of points) {
    if (p.latitude < minLat) minLat = p.latitude
    if (p.latitude > maxLat) maxLat = p.latitude
    if (p.longitude < minLng) minLng = p.longitude
    if (p.longitude > maxLng) maxLng = p.longitude
  }

  const latPad = (maxLat - minLat) * padding || 0.5
  const lngPad = (maxLng - minLng) * padding || 0.5
  const bounds = { north: maxLat + latPad, south: minLat - latPad, east: maxLng + lngPad, west: minLng - lngPad }

  const centerLat = (maxLat + minLat) / 2
  const lngSpan = bounds.east - bounds.west
  const latSpan = bounds.north - bounds.south
  const cosLat = Math.cos((centerLat * Math.PI) / 180)
  const zoom = Math.floor(
    Math.min(Math.log2(360 / Math.max(latSpan, 0.0001)), Math.log2(360 / Math.max(lngSpan * cosLat, 0.0001)))
  )

  return { bounds, zoom: Math.max(0, Math.min(15, zoom)) }
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t

/**
 * Interpolates between two coordinates over a duration using requestAnimationFrame.
 * Returns a cancel function.
 */
export const animateCoordinates = (
  from: Coordinate,
  to: Coordinate,
  durationMs: number,
  onUpdate: (coord: Coordinate) => void,
  onComplete?: () => void
): (() => void) => {
  const startTime = performance.now()
  let animationId: number | null = null

  const animate = (currentTime: number) => {
    const elapsed = currentTime - startTime
    const t = Math.min(elapsed / durationMs, 1)
    const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
    onUpdate({
      latitude: lerp(from.latitude, to.latitude, eased),
      longitude: lerp(from.longitude, to.longitude, eased),
    })
    if (t < 1) animationId = requestAnimationFrame(animate)
    else onComplete?.()
  }

  animationId = requestAnimationFrame(animate)
  return () => {
    if (animationId !== null) cancelAnimationFrame(animationId)
  }
}
